import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from './dialog.service';

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (dlg.state().open) {
      <!-- Backdrop -->
      <div class="dialog-backdrop" (click)="onBackdropClick()">
        <!-- Panel -->
        <div class="dialog-panel" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">

          <!-- Icon + Title -->
          <div class="dialog-header">
            @if (dlg.state().type === 'alert') {
              <div class="dialog-icon dialog-icon--info">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
            }
            @if (dlg.state().type === 'confirm' && !dlg.state().destructive) {
              <div class="dialog-icon dialog-icon--warn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
            }
            @if (dlg.state().type === 'confirm' && dlg.state().destructive) {
              <div class="dialog-icon dialog-icon--danger">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </div>
            }
            @if (dlg.state().type === 'prompt') {
              <div class="dialog-icon dialog-icon--info">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                </svg>
              </div>
            }
            <h2 class="dialog-title">{{ dlg.state().title }}</h2>
          </div>

          <!-- Message -->
          <p class="dialog-message">{{ dlg.state().message }}</p>

          <!-- Prompt input -->
          @if (dlg.state().type === 'prompt') {
            <input
              #promptInput
              type="text"
              class="dialog-input"
              [(ngModel)]="promptValue"
              [placeholder]="dlg.state().placeholder || ''"
              (keydown.enter)="onOk()"
              (keydown.escape)="onCancel()" />
          }

          <!-- Buttons -->
          <div class="dialog-actions">
            @if (dlg.state().type !== 'alert') {
              <button class="dialog-btn dialog-btn--ghost" type="button" (click)="onCancel()">
                {{ dlg.state().cancelText || 'Cancel' }}
              </button>
            }
            <button
              class="dialog-btn"
              [class.dialog-btn--danger]="dlg.state().destructive"
              [class.dialog-btn--primary]="!dlg.state().destructive"
              type="button"
              (click)="onOk()">
              {{ dlg.state().confirmText || (dlg.state().type === 'alert' ? 'OK' : 'Confirm') }}
            </button>
          </div>

        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      animation: fadeIn 0.15s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    @keyframes slideUp {
      from { opacity: 0; transform: translateY(12px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    .dialog-panel {
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22), 0 4px 16px rgba(0, 0, 0, 0.1);
      padding: 28px 28px 22px;
      width: 420px;
      max-width: calc(100vw - 32px);
      animation: slideUp 0.18s ease;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }

    .dialog-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .dialog-icon--info {
      background: rgba(12, 92, 72, 0.08);
      color: #0c5c48;
    }

    .dialog-icon--warn {
      background: rgba(180, 120, 0, 0.1);
      color: #b47800;
    }

    .dialog-icon--danger {
      background: rgba(200, 40, 40, 0.1);
      color: #c82828;
    }

    .dialog-title {
      font-size: 15px;
      font-weight: 700;
      color: #23292c;
      margin: 0;
      line-height: 1.3;
    }

    .dialog-message {
      font-size: 13px;
      color: #4b5557;
      line-height: 1.6;
      margin: 0 0 20px;
      white-space: pre-line;
    }

    .dialog-input {
      width: 100%;
      box-sizing: border-box;
      padding: 9px 12px;
      border: 1.5px solid #c8d2ce;
      border-radius: 7px;
      font-size: 13px;
      color: #23292c;
      outline: none;
      margin-bottom: 20px;
      transition: border-color 0.15s;
      font-family: inherit;
    }

    .dialog-input:focus {
      border-color: #0c5c48;
      box-shadow: 0 0 0 3px rgba(12, 92, 72, 0.15);
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .dialog-btn {
      padding: 8px 20px;
      border-radius: 7px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: background 0.15s, box-shadow 0.15s;
      font-family: inherit;
    }

    .dialog-btn--primary {
      background: #0c5c48;
      color: #fff;
    }

    .dialog-btn--primary:hover {
      background: #084738;
      box-shadow: 0 2px 8px rgba(12, 92, 72, 0.3);
    }

    .dialog-btn--danger {
      background: #c82828;
      color: #fff;
    }

    .dialog-btn--danger:hover {
      background: #a82020;
      box-shadow: 0 2px 8px rgba(200, 40, 40, 0.3);
    }

    .dialog-btn--ghost {
      background: #f4f6f5;
      color: #4b5557;
      border: 1.5px solid #e2e6e3;
    }

    .dialog-btn--ghost:hover {
      background: #e8ece9;
    }
  `]
})
export class DialogComponent {
  readonly dlg = inject(DialogService);
  promptValue = '';

  onOk(): void {
    const s = this.dlg.state();
    if (s.type === 'prompt') {
      this.dlg._resolve(this.promptValue);
      this.promptValue = '';
    } else if (s.type === 'confirm') {
      this.dlg._resolve(true);
    } else {
      this.dlg._resolve(null);
    }
  }

  onCancel(): void {
    this.promptValue = '';
    this.dlg._cancel();
  }

  onBackdropClick(): void {
    // Only close alert on backdrop click; confirm/prompt require explicit action
    if (this.dlg.state().type === 'alert') {
      this.dlg._resolve(null);
    }
  }
}
